import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, FormControl, InputLabel, CircularProgress, Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LockIcon from '@mui/icons-material/Lock';
import { format } from 'date-fns';
import { dmsApi } from '../services/dmsApi';
import useDmsPalette from '../hooks/useDmsPalette';

const LEVELS = ['view', 'comment', 'edit', 'admin'];

export default function PermissionEditor({ documentId }) {
  const MP = useDmsPalette();
  const LEVEL_COLORS = {
    view: MP.textSecondary,
    comment: MP.yellow,
    edit: MP.blue,
    admin: MP.green,
  };
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantLevel, setGrantLevel] = useState('view');
  const [granting, setGranting] = useState(false);

  const loadPermissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dmsApi.getPermissions(documentId);
      setPermissions(data.permissions || data || []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.error || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => { loadPermissions(); }, [loadPermissions]);

  const handleGrant = async () => {
    if (!grantEmail.trim()) return;
    setGranting(true);
    setError(null);
    try {
      await dmsApi.grantPermission(documentId, {
        email: grantEmail.trim(),
        level: grantLevel,
        resourceType: 'document',
      });
      setGrantEmail('');
      setGrantLevel('view');
      loadPermissions();
    } catch (err) {
      setError(err?.response?.data?.error || err?.error || 'Failed to grant permission');
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async (permId) => {
    try {
      await dmsApi.revokePermission(documentId, permId);
      loadPermissions();
    } catch (err) {
      setError(err?.response?.data?.error || err?.error || 'Failed to revoke permission');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleGrant();
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {permissions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3, color: MP.textSecondary, mb: 2 }}>
          <LockIcon sx={{ fontSize: 36, mb: 1 }} />
          <Typography variant="body2">No permissions granted yet. Only the owner has access.</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: MP.textSecondary, fontWeight: 600, borderColor: MP.border }}>User</TableCell>
                <TableCell sx={{ color: MP.textSecondary, fontWeight: 600, borderColor: MP.border }}>Level</TableCell>
                <TableCell sx={{ color: MP.textSecondary, fontWeight: 600, borderColor: MP.border }}>Granted By</TableCell>
                <TableCell sx={{ color: MP.textSecondary, fontWeight: 600, borderColor: MP.border }}>Date</TableCell>
                <TableCell sx={{ color: MP.textSecondary, fontWeight: 600, borderColor: MP.border }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions.map((perm) => (
                <TableRow key={perm._id} sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell sx={{ borderColor: MP.border }}>
                    <Typography variant="body2" fontWeight={500}>
                      {perm.user?.email || perm.email || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderColor: MP.border }}>
                    <Typography
                      variant="caption" fontWeight={600}
                      sx={{
                        color: LEVEL_COLORS[perm.level] || MP.white,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      {perm.level}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderColor: MP.border }}>
                    <Typography variant="caption" color="text.secondary">
                      {perm.grantedBy?.name || perm.grantedBy?.email || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderColor: MP.border }}>
                    <Typography variant="caption" color="text.secondary">
                      {perm.createdAt ? format(new Date(perm.createdAt), 'MMM d, yyyy') : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderColor: MP.border }} align="right">
                    <Tooltip title="Revoke access">
                      <IconButton size="small" color="error" onClick={() => handleRevoke(perm._id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Grant access form */}
      <Box sx={{
        display: 'flex', gap: 1.5, alignItems: 'flex-end',
        bgcolor: MP.deepBlue, border: `1px solid ${MP.border}`,
        borderRadius: 1, p: 2,
      }}>
        <PersonAddIcon sx={{ color: MP.textSecondary, mb: 1 }} />
        <TextField
          label="Email address" size="small" value={grantEmail}
          onChange={(e) => setGrantEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ flex: 1, '& .MuiOutlinedInput-root': { bgcolor: MP.surface } }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Level</InputLabel>
          <Select value={grantLevel} onChange={(e) => setGrantLevel(e.target.value)} label="Level">
            {LEVELS.map(level => (
              <MenuItem key={level} value={level} sx={{ textTransform: 'capitalize' }}>
                {level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained" onClick={handleGrant} size="small"
          disabled={!grantEmail.trim() || granting}
          sx={{ minWidth: 100, py: 1 }}
        >
          {granting ? <CircularProgress size={18} color="inherit" /> : 'Grant'}
        </Button>
      </Box>
    </Box>
  );
}
